import type { MDXComponents } from 'mdx/types';
import Image from 'next/image';

export function useMDXComponents(components: MDXComponents): MDXComponents {
    return {
        // Headings
        h1: ({ children }) => (
            <h1 className="text-4xl font-bold text-gray-900 mb-6 mt-8 first:mt-0">
                {children}
            </h1>
        ),
        h2: ({ children }) => (
            <h2 className="text-3xl font-semibold text-gray-800 mb-4 mt-8">
                {children}
            </h2>
        ),
        h3: ({ children }) => (
            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">
                {children}
            </h3>
        ),
        h4: ({ children }) => (
            <h4 className="text-xl font-semibold text-gray-800 mb-2 mt-4">
                {children}
            </h4>
        ),

        // Paragraphs and text
        p: ({ children }) => (
            <p className="text-gray-700 leading-relaxed mb-4">
                {children}
            </p>
        ),

        // Lists
        ul: ({ children }) => (
            <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                {children}
            </ul>
        ),
        ol: ({ children }) => (
            <ol className="list-decimal list-inside text-gray-700 mb-4 space-y-2">
                {children}
            </ol>
        ),
        li: ({ children }) => (
            <li className="ml-4">
                {children}
            </li>
        ),

        // Links
        a: ({ href, children }) => (
            <a
                href={href}
                className="text-blue-600 hover:text-blue-800 underline transition-colors"
                target={href?.startsWith('http') ? '_blank' : undefined}
                rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
            >
                {children}
            </a>
        ),

        // Code
        code: ({ children }) => (
            <code className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm font-mono">
                {children}
            </code>
        ),
        pre: ({ children }) => (
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto mb-4">
                {children}
            </pre>
        ),

        // Blockquotes
        blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-600 mb-4">
                {children}
            </blockquote>
        ),

        // Images - Using Next.js optimized Image component
        img: ({ src, alt, width, height, ...props }) => {
            if (!src) return null;

            // For external images, use Next.js Image component with unoptimized prop
            if (src.startsWith('http')) {
                return (
                    <div className="mb-4">
                        <Image
                            src={src}
                            alt={alt || ''}
                            width={width || 800}
                            height={height || 600}
                            className="max-w-full h-auto rounded-lg shadow-md"
                            unoptimized
                            {...props}
                        />
                    </div>
                );
            }

            // For local images, use Next.js Image component
            return (
                <div className="mb-4">
                    <Image
                        src={src}
                        alt={alt || ''}
                        width={width || 800}
                        height={height || 600}
                        className="max-w-full h-auto rounded-lg shadow-md"
                        {...props}
                    />
                </div>
            );
        },

        // Tables
        table: ({ children }) => (
            <div className="overflow-x-auto mb-4">
                <table className="min-w-full border-collapse border border-gray-300">
                    {children}
                </table>
            </div>
        ),
        th: ({ children }) => (
            <th className="border border-gray-300 bg-gray-100 px-4 py-2 text-left font-semibold">
                {children}
            </th>
        ),
        td: ({ children }) => (
            <td className="border border-gray-300 px-4 py-2">
                {children}
            </td>
        ),

        // Horizontal rule
        hr: () => (
            <hr className="border-gray-300 my-8" />
        ),

        // Strong/bold text
        strong: ({ children }) => (
            <strong className="font-semibold text-gray-900">
                {children}
            </strong>
        ),

        // Emphasis/italic text
        em: ({ children }) => (
            <em className="italic text-gray-800">
                {children}
            </em>
        ),

        ...components,
    };
} 